import React, { useEffect, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAppApi } from "@/App";
import { ModeratorLogKind } from "@/const";
import { useRequest } from "@components/AppHook";
import ModeratorLogKindDisplay from "@components/Displays/ModeratorLogKindDisplay";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal } from "@components/Helpers";
import { ListingCard } from "@components/Pages";
import { parseAndLinkify, url_reverse } from "@components/Utils";

export default function ModeratorCommentList({ header, apiUrl }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(apiUrl);
    const [show, setShow] = useState(false);
    const [comment, setComment] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const onFormSubmit = useSubmit();
    const { api_post } = useRequest();

    if (!apiData) return;

    const deleteComment = (id) => {
        if (window.confirm(t("Вы уверены, что хотите удалить данный комментарий?"))) {
            api_post(null, apiUrl, {
                params: { action: "delete_comment", id },
                then: (resp) => {
                    if (resp.status === "ok") {
                        setApiData(resp);
                    }
                },
            });
        }
    };
    const showEditModal = (modalComment) => {
        setComment(modalComment);
        setShow(true);
    };

    const EditModal = () => {
        const [newComment, setNewComment] = useState(comment);

        const handleClose = () => {
            setShow(false);
        };
        const handleComment = (value) => {
            const c = { ...newComment };
            c.comment = value;
            setNewComment(c);
        };
        const editComment = (ev) => {
            api_post(ev, apiUrl, {
                params: { action: "edit_comment", id: newComment.id, comment: newComment.comment },
                then: (resp) => {
                    if (resp.status === "ok") {
                        setApiData(resp);
                        setShow(false);
                    }
                },
            });
        };

        return (
            <DevModal
                size="lg"
                show={show}
                onHide={handleClose}
                header={t("Редактирование")}
                body={
                    <DevForm.TextArea
                        name="comment"
                        value={newComment.comment}
                        onChange={(ev) => handleComment(ev.target.value)}
                    />
                }
                footer={
                    <>
                        <DevForm.Button variant="primary" name="submit" onClick={editComment}>
                            {t("Отправить")}
                        </DevForm.Button>
                        <DevForm.Button variant="secondary" name="cancel" onClick={handleClose}>
                            {t("Отмена")}
                        </DevForm.Button>
                    </>
                }
            />
        );
    };

    const headerTools = (
        <Button
            size="sm"
            variant="dark"
            onClick={() => {
                const newApiData = { ...apiData };
                newApiData.forms_data.main.errors = {};
                setApiData(newApiData);
                setShowCreateModal(true);
            }}
        >
            <Awesome type="plus-circle" /> {t("Добавить")}
        </Button>
    );

    return (
        <>
            <ListingCard header={header} headerTools={headerTools}>
                <Table hover responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th>{t("Аккаунт")}</th>
                            <th>{t("Статус")}</th>
                            {apiData.mlog_type === "developer" && <th>{t("Результат")}</th>}
                            <th>{t("Тип")}</th>
                            <th>{t("Комментарий")}</th>
                            <th style={{ width: "10%" }} />
                        </tr>
                    </thead>
                    <tbody>
                        {apiData.object_list?.map((o, i) => (
                            <tr key={`object_list${i}`}>
                                <td>
                                    <Link to={url_reverse("admin:account:edit", { acc_id: o.moderator_id })}>
                                        {o.moderator_name}
                                    </Link>
                                    <br />
                                    {o.date_added}
                                </td>
                                <td>{o.mlog_state}</td>
                                {apiData.mlog_type === "developer" && <td> {o.mlog_result}</td>}
                                <td>
                                    <ModeratorLogKindDisplay kind={o.mlog_kind} />
                                </td>
                                <td className="show-wrap-text">{parseAndLinkify(o.comment)}</td>
                                <td className="dmg-table-row-tools">
                                    {o.mlog_kind === ModeratorLogKind.SERVICE && (
                                        <>
                                            <Awesome
                                                type="pencil"
                                                color="dodgerblue"
                                                onClick={() => showEditModal(o)}
                                            />{" "}
                                            <Awesome type="trash-o" color="red" onClick={() => deleteComment(o.id)} />
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                <DevModal
                    size="lg"
                    show={showCreateModal}
                    onHide={() => setShowCreateModal(false)}
                    header={t("Новый комментарий")}
                    body={<RenderForm apiData={apiData} setApiData={setApiData} bareForm={true} />}
                    footer={
                        <DevForm.Button type="submit" name="action" value="add_manual_comment">
                            {t("Сохранить")}
                        </DevForm.Button>
                    }
                    renderWrapper={(children) => (
                        <Form
                            onSubmit={(ev) =>
                                onFormSubmit(ev, {
                                    onOK: (resp) => {
                                        resp.forms_data.main.data = {};
                                        resp.forms_data.main.errors = {};
                                        setApiData(resp);
                                        setShowCreateModal(false);
                                    },
                                })
                            }
                        >
                            {children}
                        </Form>
                    )}
                />
                {show && <EditModal />}
            </ListingCard>
        </>
    );
}

export function ModeratorCommentsEmbedded({ gameId, filter }) {
    const { t } = useTranslation();
    const apiUrl = url_reverse("api:game:moderator_comments", { game_id: gameId });
    const [apiData, setApiData] = useState({});
    const { api_get } = useRequest();
    const filter_query = filter ? "?state=" + filter : "";

    useEffect(() => {
        api_get(null, apiUrl + filter_query, {
            then: (resp) => {
                setApiData(resp);
            },
        });
    }, []);

    if (!apiData) return;

    return (
        <ListingCard header={t("Комментарии")}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Аккаунт")}</th>
                        <th>{t("Статус")}</th>
                        {apiData.mlog_type === "developer" && <th>{t("Результат")}</th>}
                        <th>{t("Тип")}</th>
                        <th>{t("Комментарий")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o, i) => (
                        <tr key={`object_list${i}`}>
                            <td>
                                <Link to={url_reverse("admin:account:edit", { acc_id: o.moderator_id })}>
                                    {o.moderator_name}
                                </Link>
                                <br />
                                {o.date_added}
                            </td>
                            <td>{o.mlog_state}</td>
                            {apiData.mlog_type === "developer" && <td> {o.mlog_result}</td>}
                            <td>
                                <ModeratorLogKindDisplay kind={o.mlog_kind} />
                            </td>
                            <td className="show-wrap-text">{parseAndLinkify(o.comment)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ListingCard>
    );
}
